import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { Box, Typography, IconButton, Chip, Tooltip, Alert } from '@mui/material';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';

import { RaptorJobFormValues, Group, GroupEmployee } from '../../../RaptorJobValidationSchema';
import { EquipmentStatusChip } from '@/equipments/presentation/components/EquipmentStatusChip';
import { CrewAssignmentStatus } from '@/common/infrastructure/gql/generated/schema';
import { useEffect } from 'react';
import { useCreateJobGroups } from '../hook/useCreateJobGroups';
import tid from '@/common/infrastructure/config/tid';

export const CreateJobGroups = <T extends { groups: Group[] } = RaptorJobFormValues>({
  onUnavailableResources
}: {
  onUnavailableResources?: (hasUnavailableResources: boolean) => void;
}) => {
  const { hasUnavailableResources, isMobile, setFieldValue, values } = useCreateJobGroups<T>();

  useEffect(() => {
    onUnavailableResources?.(hasUnavailableResources);
  }, [hasUnavailableResources]);

  if (values.groups.length === 0) {
    return null;
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: 2 }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700 }}>
          Added Groups ({values.groups.length})
        </Typography>
      </Box>
      {hasUnavailableResources && (
        <Alert severity="warning" variant="outlined">
          You have added groups with unavailable resources
        </Alert>
      )}
      {values.groups.map((group: Group) => (
        <Box
          key={group.id}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
            backgroundColor: 'background.paper'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              p: 2
            }}
          >
            {/* Equipment Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 1 }}>
                <LocalShippingOutlinedIcon />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Equipment
                </Typography>
              </Box>
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1,
                    maxWidth: '150px'
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {group.equipment.equipmentName}
                  </Typography>
                  {group.equipment.equipmentStatus &&
                    group.equipment.equipmentStatus.toLowerCase() !== 'available' &&
                    group.equipment.equipmentStatus.toLowerCase() !== 'in service' && (
                      <EquipmentStatusChip status={group.equipment.equipmentStatus} />
                    )}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  {group.equipment.equipmentClass} | {group.equipment.equipmentType} | {group.equipment.equipmentSize}
                </Typography>
              </Box>
            </Box>

            {/* Employees Section */}
            <Box>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600, mb: 1 }}>
                <PeopleOutlineOutlinedIcon />
                Crew
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                {group.employees.slice(0, isMobile ? 1 : 3).map((employee: GroupEmployee, index: number) => {
                  const isBanned = employee.status?.includes(CrewAssignmentStatus.Banned);
                  const isOutOfOffice = employee.status?.includes(CrewAssignmentStatus.OutOfOffice);

                  return (
                    <Box key={index}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {employee.employeeName || 'Unassigned'}
                        </Typography>
                        {isBanned && (
                          <Chip
                            label="BANNED"
                            size="small"
                            sx={{
                              backgroundColor: 'error.main',
                              color: 'common.white',
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              height: '16px',
                              '& .MuiChip-label': {
                                px: 0.5
                              }
                            }}
                          />
                        )}
                        {isOutOfOffice && !isBanned && (
                          <Chip
                            label="OUT OF OFFICE"
                            size="small"
                            sx={{
                              backgroundColor: 'warning.main',
                              color: 'common.white',
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              height: '16px',
                              '& .MuiChip-label': {
                                px: 0.5
                              }
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="body2">{employee.roleName}</Typography>
                    </Box>
                  );
                })}
                {(isMobile ? group.employees.length > 1 : group.employees.length > 3) && (
                  <Tooltip
                    title={
                      <Box>
                        {group.employees.slice(isMobile ? 1 : 3).map((employee: GroupEmployee, index: number) => {
                          const isBanned = employee.status?.includes(CrewAssignmentStatus.Banned);
                          const isOutOfOffice = employee.status?.includes(CrewAssignmentStatus.OutOfOffice);

                          return (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                {employee.employeeName || 'Unassigned'} - {employee.roleName}
                              </Typography>
                              {isBanned && (
                                <Chip
                                  label="BANNED"
                                  size="small"
                                  sx={{
                                    backgroundColor: 'error.main',
                                    color: 'common.white',
                                    fontWeight: 600,
                                    fontSize: '0.6rem',
                                    height: '14px',
                                    '& .MuiChip-label': {
                                      px: 0.5
                                    }
                                  }}
                                />
                              )}
                              {isOutOfOffice && !isBanned && (
                                <Chip
                                  label="OUT OF OFFICE"
                                  size="small"
                                  sx={{
                                    backgroundColor: 'warning.main',
                                    color: 'common.white',
                                    fontWeight: 600,
                                    fontSize: '0.6rem',
                                    height: '14px',
                                    '& .MuiChip-label': {
                                      px: 0.5
                                    }
                                  }}
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    }
                    arrow
                    placement="top"
                  >
                    <Chip
                      label={`+${group.employees.length - (isMobile ? 1 : 3)}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        color: (theme) => theme.palette.primary.contrastText,
                        cursor: 'pointer',
                        backgroundColor: (theme) => theme.palette.primary.main
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
          <Box sx={{ alignSelf: 'center' }}>
            <IconButton
              onClick={() =>
                setFieldValue(
                  'groups',
                  values.groups.filter((g: Group) => g.id !== group.id)
                )
              }
              sx={{ color: 'error.main' }}
              {...tid(`jobs:create:remove-group-button-${group.id}`)}
            >
              <DeleteOutlinedIcon />
            </IconButton>
          </Box>
        </Box>
      ))}
    </>
  );
};
